import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import moment from 'moment';
import { TemplateUsage as TemplateType } from '@bb/domain/dist/types/Template';
import { Tag, ListItem } from 'components';
import { Template } from 'api/TemplateController';

export interface TemplateListCellAuth {
  firstName: string;
  lastName: string;
  org: {
    locations: { name: string }[];
  };
}

export interface TemplateListCellProps {
  template: Template;
  selected?: boolean;
  onClick?: () => void;
  auth: TemplateListCellAuth;
  dataCy?: string;
}

const TemplateListCell = (props: TemplateListCellProps) => {
  const {
    template: { isDefault, location, name, team, type, role, updatedAt, teams },
    selected,
    onClick,
    auth,
    dataCy,
  } = props;

  interface Team {
    id: string;
    name: string;
  }
  const [allTeams, setAllTeams] = useState<Team[] | undefined>();

  useEffect(() => {
    const fetchTeams = async () => {
      const { data } = await axios.get<Team[]>('/team');
      setAllTeams(data);
    };

    if (team) fetchTeams();
  }, [team]);

  const getTeamName = (id: string) =>
    (allTeams && allTeams.find(t => t.id === id)?.name) || '';

  const tagLabels: Record<TemplateType, string> = {
    feedback: 'Feedback Request',
    feedbackSurvey: 'Feedback Survey',
    propertyAnnouncement: 'Message Showing Agents',
    offer: 'Offer Registration',
    emailSignature: 'Email Signature',
    sendListing: 'Send a Listing',
  };

  const scopeText = useMemo(() => {
    switch (role) {
      case 'GLOBAL':
        return 'Entire Brokerage';
      case 'BRANCH':
        return location?.length
          ? auth.org.locations[Number(location[0])]?.name
          : '';
      case 'TEAM':
        return teams?.map(getTeamName).join(', ');
      case 'USER':
        return `${auth.firstName} ${auth.lastName}`;
      default:
        return '';
    }
  }, [role, location, team, allTeams]);

  const scopeDefaultText = useMemo(() => {
    switch (role) {
      case 'GLOBAL':
        return 'Global Default';
      case 'BRANCH':
        return 'Branch Default';
      case 'TEAM':
        return 'Team Default';
      case 'USER':
      default:
        return 'Default';
    }
  }, [role, location, team]);

  return (
    <StyledListItem selected={selected} onClick={onClick} data-cy={dataCy}>
      <TagsContainer>
        {isDefault && <Tag color="primary">{scopeDefaultText}</Tag>}
        <Tag color="textPrimary">{tagLabels[type]}</Tag>
      </TagsContainer>
      <DateContainer>
        {updatedAt ? moment(updatedAt).format('MMMM D, h:mma') : ''}
      </DateContainer>
      <NameContainer>{name}</NameContainer>
      <ScopeContainer>{scopeText}</ScopeContainer>
    </StyledListItem>
  );
};

export default TemplateListCell;

const StyledListItem = styled(ListItem)<{ selected?: boolean }>`
  padding: 16px 24px;
  background-color: ${({ selected }) => (selected ? '#E8F0FF' : '#ffffff')};
  /* Grey / grey-4 */
  border-bottom: 1px solid #e8e8e8;

  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;

  cursor: pointer;
`;

const TagsContainer = styled.div`
  margin-bottom: 6px;
`;
const DateContainer = styled.div`
  /* Grey / grey-7 */
  color: #8c8c8c;
`;
const NameContainer = styled.div`
  flex-basis: 100%;
  font-weight: 600;
`;
const ScopeContainer = styled.div`
  flex-basis: 100%;
`;
